import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Button, Group, Loader, Select, Stack, Text } from '@mantine/core';
import { useForm, type UseFormReturnType } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { emissionReportImportsApi } from '../../services/emissionReportImportsApi';
import { plantKilnsApi } from '../../services/plantKilnsApi';
import { environmentalDataApi } from '../../services/reportsApi';
import {
  getApiErrorMessage,
  getApiViolations,
} from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';
import type { EmissionReportImport, Plant } from '../../types/entities';
import {
  IMPORTABLE_REPORT_STATUSES,
  filteredOutNote,
  isAssignmentComplete,
  kilnSlot,
  measurementYear,
  reportChoiceLabel,
  reportLabel,
  reportSlot,
  type KilnChoice,
  type ReportChoice,
  type Slot,
  type SlotKind,
} from './cascadeModel';

/**
 * Die Zuordnungskaskade einer offenen Listenzeile (Sketch 019): Werk zuerst, danach Ofen und
 * Meldung -- beide haengen am Werk, nicht voneinander.
 *
 * Die Anlage 1 nennt weder Unternehmen noch Werk noch Ofen. Die Zuordnung ist damit der
 * eigentliche Handgriff des Bearbeiters und die Stelle, an der ein Fehlgriff Messwerte an die
 * falsche Stelle schreibt -- deshalb wird so scharf gefiltert, dass am Ende oft nur eine
 * Entscheidung uebrig bleibt, und wo keine uebrig bleibt, gesetzt statt gefragt wird.
 *
 * Alle drei Mengen kommen SERVERSEITIG gefiltert: Werke ueber `kilns.allowsOwnMeasurements=false`
 * (einmal je Seite, vom Aufrufer), Oefen ueber `plant` + `allowsOwnMeasurements=false`, Meldungen
 * ueber Werk, offene Staende und den Jahresbereich auf `reportingPeriod`. Nachgefiltert wird im
 * Browser nichts -- die einzige Grenze, die der Server nicht kennt (Ofenaktivitaet am Stichtag),
 * zieht `cascadeModel`.
 */

interface AssignmentValues {
  plant: string | null;
  plantKiln: string | null;
  environmentalData: string | null;
}

export interface AssignmentCascadeProps {
  entry: EmissionReportImport;
  /** Werke mit mindestens einem Ofen, an dem der VDZ misst -- einmal je Seite geladen. */
  plants: Plant[];
  plantsLoading: boolean;
  /**
   * Meldet, ob an dieser Zeile gerade gearbeitet wird (`form.isDirty()`). Die Liste unterdrueckt
   * daraufhin ihren Zusatztext: "Werk und Ofen stehen, die Meldung nicht" ist als Datenlage
   * nicht davon zu unterscheiden, dass der Bearbeiter mitten in der Kaskade steht (Hinweis aus
   * Plan 35-09).
   */
  onDirtyChange: (entryId: number, dirty: boolean) => void;
  onDiscard: (entry: EmissionReportImport) => void;
}

function plantIdFromIri(iri: string): number {
  return Number(iri.split('/').pop());
}

function kilnIri(kiln: KilnChoice): string {
  return `/api/plant_kilns/${kiln.id}`;
}

function reportIri(report: ReportChoice): string {
  return `/api/environmental_datas/${report.id}`;
}

/**
 * Der Formularwert eines Feldes nach dem Laden: `fixed` wird GESETZT (nicht ausgewaehlt, aber
 * gesetzt), `empty` wird geleert, und bei `choose` bleibt ein vorhandener Wert nur stehen, wenn
 * er noch angeboten wird -- nach einem Werkswechsel passt der alte Verweis sonst nicht mehr.
 */
function nextSlotValue<T>(
  slot: Slot<T>,
  current: string | null,
  toIri: (value: T) => string
): string | null {
  if (slot.kind === 'empty') {
    return null;
  }
  if (slot.kind === 'fixed') {
    return toIri(slot.value);
  }

  return current !== null &&
    slot.options.some((option) => toIri(option) === current)
    ? current
    : null;
}

export function AssignmentCascade({
  entry,
  plants,
  plantsLoading,
  onDirtyChange,
  onDiscard,
}: AssignmentCascadeProps) {
  const navigate = useNavigate();

  // Keine `validate`-Konfiguration: Feldfehler kommen ausschliesslich aus dem Backend
  // (Projektregel). Modus `controlled`, weil der Vorschau-Knopf am Formularzustand haengt --
  // im uncontrolled-Modus zoege das zwischen den Renders nicht nach.
  const form = useForm<AssignmentValues>({
    mode: 'controlled',
    // Die Select-Werte sind IRI-Zeichenketten (so geht die Zuordnung auch wieder hinaus);
    // der geladene Eintrag traegt die drei Verweise als eingebettete Objekte. `@id` ist die
    // Bruecke zwischen beiden Formen.
    initialValues: {
      plant: entry.plant?.['@id'] ?? null,
      plantKiln: entry.plantKiln?.['@id'] ?? null,
      environmentalData: entry.environmentalData?.['@id'] ?? null,
    },
  });

  const [kilns, setKilns] = useState<KilnChoice[] | null>(null);
  const [reports, setReports] = useState<ReportChoice[] | null>(null);
  const [loadingChoices, setLoadingChoices] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const values = form.getValues();
  const selectedPlant = values.plant;
  const year = measurementYear(entry.measurementRangeEnd);

  /**
   * Nach der Werkswahl (oder gleich beim Aufbau einer Zeile, die schon ein Werk traegt) werden
   * Oefen und Meldungen dieses Werks geholt und die abhaengigen Felder nachgezogen.
   *
   * Der Effekt haengt AUSSCHLIESSLICH an Werk und Messzeitraum -- bewusst nicht an `form`.
   * Die Referenz ist ueber die Lebenszeit der Zeile stabil, aber ein Effekt, der eine
   * Formularfunktion in seiner Abhaengigkeitsliste fuehrt und selbst einen Formularwert setzt,
   * haenge an der Memoisierung der Bibliothek: waere sie einmal nicht gegeben, liefe hier eine
   * Schleife aus Nachladen und Neuzeichnen. So laeuft der Abruf genau einmal je Werkswahl.
   */
  useEffect(() => {
    if (selectedPlant === null) {
      setKilns(null);
      setReports(null);
      setLoadError(null);
      return;
    }

    let cancelled = false;
    setLoadingChoices(true);
    setLoadError(null);

    void (async () => {
      const plantId = plantIdFromIri(selectedPlant);
      try {
        const [loadedKilns, loadedReports] = await Promise.all([
          plantKilnsApi.getByPlant(plantId, { allowsOwnMeasurements: false }),
          year === null
            ? Promise.resolve([])
            : environmentalDataApi.getAll({
                plant: plantId,
                status: IMPORTABLE_REPORT_STATUSES,
                reportingPeriodAfter: `${year}-01-01`,
                reportingPeriodBefore: `${year}-12-31`,
              }),
        ]);

        if (cancelled) {
          return;
        }

        setKilns(loadedKilns);
        setReports(loadedReports);

        const nextKilnSlot = kilnSlot(
          loadedKilns,
          entry.measurementRangeEnd
        ).slot;
        const nextReportSlot =
          year === null
            ? ({ kind: 'empty', message: '' } as Slot<ReportChoice>)
            : reportSlot(loadedReports, year);
        const current = form.getValues();
        form.setFieldValue(
          'plantKiln',
          nextSlotValue(nextKilnSlot, current.plantKiln, kilnIri)
        );
        form.setFieldValue(
          'environmentalData',
          nextSlotValue(nextReportSlot, current.environmentalData, reportIri)
        );
      } catch (err) {
        if (!cancelled) {
          setKilns([]);
          setReports([]);
          setLoadError(
            getApiErrorMessage(
              err,
              'Öfen und Meldungen dieses Werks konnten nicht geladen werden.'
            )
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingChoices(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlant, year, entry.measurementRangeEnd]);

  // "Hat sich etwas geaendert" beantwortet `form.isDirty()` -- keine zweite Vergleichsfunktion
  // daneben (Projektregel).
  const dirty = form.isDirty();
  useEffect(() => {
    onDirtyChange(entry.id, dirty);
  }, [dirty, entry.id, onDirtyChange]);

  useEffect(
    () => () => {
      onDirtyChange(entry.id, false);
    },
    [entry.id, onDirtyChange]
  );

  const kilnResult =
    kilns === null ? null : kilnSlot(kilns, entry.measurementRangeEnd);
  const reportResult =
    reports === null || year === null ? null : reportSlot(reports, year);

  const kilnKind: SlotKind | null = kilnResult?.slot.kind ?? null;
  const reportKind: SlotKind | null = reportResult?.kind ?? null;

  const complete = isAssignmentComplete({
    plant: values.plant,
    plantKiln: values.plantKiln,
    environmentalData: values.environmentalData,
    kilnKind,
    reportKind,
  });

  const handleSubmit = useCallback(
    async (submitted: AssignmentValues): Promise<void> => {
      setSaving(true);
      try {
        // Steht die Zuordnung schon so in der Datenbank, wird nicht erneut geschrieben -- der
        // Knopf fuehrt dann nur zur Vorschau.
        if (form.isDirty()) {
          await emissionReportImportsApi.update(entry.id, {
            plant: submitted.plant,
            plantKiln: submitted.plantKiln,
            environmentalData: submitted.environmentalData,
          });
        }
        void navigate(`/emission-reports/${entry.id}`);
      } catch (err) {
        form.setErrors(mapViolations(getApiViolations(err)));
        notifications.show({
          title: 'Zuordnung nicht gespeichert',
          message: getApiErrorMessage(
            err,
            'Die Zuordnung konnte nicht gespeichert werden.'
          ),
          color: 'red',
        });
      } finally {
        setSaving(false);
      }
    },
    [entry.id, form, navigate]
  );

  const kilnNote =
    kilnResult === null ? null : filteredOutNote(kilnResult.filteredOutCount);

  return (
    <form onSubmit={form.onSubmit((submitted) => void handleSubmit(submitted))}>
      <Group align="flex-start" gap="md" wrap="wrap">
        <Box style={{ flex: '1 1 220px', minWidth: 180 }}>
          <Select
            label="Werk"
            placeholder={plantsLoading ? 'wird geladen…' : 'Werk wählen…'}
            data={plants.map((plant) => ({
              value: `/api/plants/${plant.id}`,
              label: plant.name,
            }))}
            disabled={plantsLoading}
            searchable
            size="xs"
            {...form.getInputProps('plant')}
          />
        </Box>

        <Box style={{ flex: '1 1 220px', minWidth: 180 }}>
          <SlotField
            label="Ofen"
            form={form}
            name="plantKiln"
            slot={kilnResult?.slot ?? null}
            loading={loadingChoices}
            waitingForPlant={selectedPlant === null}
            optionValue={kilnIri}
            optionLabel={(kiln) => kiln.name}
          />
          {kilnNote !== null && (
            <Text size="xs" c="dimmed" mt={4}>
              {kilnNote}
            </Text>
          )}
        </Box>

        <Box style={{ flex: '1 1 240px', minWidth: 200 }}>
          <SlotField
            label="Meldung"
            form={form}
            name="environmentalData"
            slot={reportResult}
            loading={loadingChoices}
            waitingForPlant={selectedPlant === null}
            optionValue={reportIri}
            optionLabel={reportChoiceLabel}
            fixedLabel={reportLabel}
          />
        </Box>

        <Group gap="xs" wrap="nowrap" pt={22}>
          <Button
            size="xs"
            variant="default"
            onClick={() => onDiscard(entry)}
            disabled={saving}
          >
            Verwerfen
          </Button>
          <Button
            size="xs"
            type="submit"
            loading={saving}
            disabled={!complete}
            title={
              complete ? undefined : 'Erst zuordnen: Werk, Ofen und Meldung.'
            }
          >
            Vorschau
          </Button>
        </Group>
      </Group>

      {loadError !== null && (
        <Text size="xs" c="red.7" mt="xs">
          {loadError}
        </Text>
      )}
    </form>
  );
}

interface SlotFieldProps<T> {
  label: string;
  /** Das Formular selbst, damit das Feld ueber `form.getInputProps(name)` haengt (Projektregel). */
  form: UseFormReturnType<AssignmentValues>;
  name: 'plantKiln' | 'environmentalData';
  slot: Slot<T> | null;
  loading: boolean;
  waitingForPlant: boolean;
  optionValue: (value: T) => string;
  optionLabel: (value: T) => string;
  /** Beschriftung im Fall `fixed`, falls sie knapper ausfaellt als im Auswahlfeld. */
  fixedLabel?: (value: T) => string;
}

/**
 * Ein Kaskadenfeld in seinen drei Zustaenden: `choose` zeigt ein Auswahlfeld, `fixed` den Wert
 * als Text mit der Begruendung darunter, `empty` den Leerfallsatz statt einer leeren Liste.
 */
function SlotField<T>({
  label,
  form,
  name,
  slot,
  loading,
  waitingForPlant,
  optionValue,
  optionLabel,
  fixedLabel,
}: SlotFieldProps<T>) {
  const error = form.errors[name];

  if (waitingForPlant) {
    return (
      <Select
        label={label}
        placeholder="erst Werk wählen"
        data={[]}
        disabled
        size="xs"
      />
    );
  }

  if (loading || slot === null) {
    return (
      <Stack gap={4}>
        <Text size="xs" fw={500}>
          {label}
        </Text>
        <Group gap="xs">
          <Loader size="xs" />
          <Text size="xs" c="dimmed">
            wird geladen…
          </Text>
        </Group>
      </Stack>
    );
  }

  if (slot.kind === 'empty') {
    return (
      <Stack gap={4}>
        <Text size="xs" fw={500}>
          {label}
        </Text>
        <Text size="xs" c="orange.8">
          {slot.message}
        </Text>
      </Stack>
    );
  }

  if (slot.kind === 'fixed') {
    return (
      <Stack gap={0}>
        <Text size="xs" fw={500}>
          {label}
        </Text>
        <Text size="sm">{(fixedLabel ?? optionLabel)(slot.value)}</Text>
        <Text size="xs" c="dimmed">
          {slot.reason}
        </Text>
        {error && (
          <Text size="xs" c="red.7">
            {error}
          </Text>
        )}
      </Stack>
    );
  }

  return (
    <Select
      label={label}
      placeholder={`${label} wählen…`}
      data={slot.options.map((option) => ({
        value: optionValue(option),
        label: optionLabel(option),
      }))}
      size="xs"
      {...form.getInputProps(name)}
    />
  );
}
